import { CURRENCIES, RATES_DATE, useCurrency } from '../lib/currency';
import { LANGS, useLanguage } from '../i18n';
import { XIcon } from './icons';

/**
 * Shown once, under the header, when the currency and/or the language was
 * picked from the visitor's country rather than by them: says what
 * happened and offers the way back. Disappears for good on dismiss or on
 * any manual choice.
 *
 * It appears after the page has loaded (the country comes from /api/geo), so it must not push the page
 * down: it hangs over the top of the content from a zero-height box in the flow (layout shift 0.12 on
 * every page for a visitor outside the US, measured 2026-10-23). The box is always there, so the server's
 * markup and the first client render agree.
 */
const CurrencyNote = () => {
  const { auto: autoCurrency, setCurrency, dismissAuto: dismissCurrency } = useCurrency();
  const { auto: autoLang, setLang, dismissAuto: dismissLang, t } = useLanguage();
  if (!autoCurrency && !autoLang) return <div className="relative z-30 h-0" />;
  const c = CURRENCIES.find((x) => x.code === autoCurrency);
  const l = LANGS.find((x) => x.code === autoLang);
  const dismiss = () => {
    dismissCurrency();
    dismissLang();
  };
  return (
    <div className="relative z-30 h-0">
    <div role="status" className="absolute inset-x-0 top-0 border-b border-border bg-card shadow-md">
      <div className="shell flex flex-wrap items-center gap-x-3 gap-y-2 py-2 text-[13px]">
        <span className="min-w-0 basis-full leading-snug text-foreground/80 sm:flex-1 sm:basis-0">
          {autoCurrency && c && (
            <>
              {/* "You pay in USD" was not true for every partner: HelloTickets' international
                  storefronts can charge in local currency. What holds for all: the marketplace
                  shows its own currency at checkout. */}
              {RATES_DATE
                ? t('Prices shown in {currency}, converted from USD at the {date} rate. The marketplace shows its own currency at checkout.', {
                    currency: t(c.name),
                    date: RATES_DATE,
                  })
                : t('Prices shown in {currency}, converted from USD at today’s rate. The marketplace shows its own currency at checkout.', {
                    currency: t(c.name),
                  })}
            </>
          )}
          {autoCurrency && autoLang && ' '}
          {autoLang && l && t('This site is shown in {language}.', { language: l.name })}
        </span>
        {autoCurrency && (
          <button
            type="button"
            onClick={() => setCurrency('USD')}
            className="shrink-0 rounded-md border border-border bg-card px-2.5 py-1 font-medium transition-colors hover:border-foreground/30"
          >
            {t('Show USD')}
          </button>
        )}
        {autoLang && (
          <button
            type="button"
            onClick={() => setLang('en')}
            className="shrink-0 rounded-md border border-border bg-card px-2.5 py-1 font-medium transition-colors hover:border-foreground/30"
          >
            {t('Show in English')}
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('Dismiss')}
          className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-foreground sm:ml-0"
        >
          <XIcon className="size-3.5" />
        </button>
      </div>
    </div>
    </div>
  );
};

export default CurrencyNote;
